import {
  type ComponentProps,
  type CSSProperties,
  type FC,
  type ReactNode,
  useState,
} from 'react'
import { LockOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import cn from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import CascadingDropdown from '@UI/New/CascadingDropdown'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'
import type {
  CrmProviderChatMenuAccount,
  CrmProviderChatMenuChannel,
  CrmProviderChatMenuDialog,
} from '@pages/Admin/CRM/utils/leadProviderChatMenu'
import { countLeadProviderChatMenuDialogs } from '@pages/Admin/CRM/utils/leadProviderChatMenu'
import { WazzupChannelTypeMark } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/WazzupChannelTypeMark'
import WazzupIcon from '@pages/Admin/CRM/components/PhoneIntegrationButtons/WazzupIcon'
import classes from './index.module.css'

export type CrmProviderChatButtonProps = {
  icon: FC<React.SVGProps<SVGSVGElement>>
  iconColor: string
  disabledIconColor?: string
  /** Подпись для aria / тултипа */
  label: string
  enabled: boolean
  /** Тултип, когда кнопка disabled */
  disabledTooltip?: string | null
  /** Тултип при hover */
  enabledTooltip?: string | null
  unreadLabel: string | null
  badgeSize?: 'table' | 'card' | 'kanban'
  /** Дерево: leaf или (legacy) account → channels → dialogs */
  menuAccounts: CrmProviderChatMenuAccount[]
  accessDeniedTooltip?: string
  tooltipProps?: ComponentProps<typeof TooltipWrapper>
  onOpenChat?: (options?: { conversationId?: number }) => void
}

const LockIcon = LockOutlined as unknown as FC<{ className?: string }>

/** Иконка провайдера + unread; меню через переиспользуемый CascadingDropdown. */
const CrmProviderChatButton: FC<CrmProviderChatButtonProps> = ({
  icon: Icon,
  iconColor,
  disabledIconColor = 'var(--color-button-icon-disabled-soft)',
  label,
  enabled,
  disabledTooltip,
  enabledTooltip,
  unreadLabel,
  badgeSize = 'table',
  menuAccounts,
  accessDeniedTooltip = '',
  tooltipProps,
  onOpenChat,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dialogCount = countLeadProviderChatMenuDialogs(menuAccounts)

  const openConversation = (conversationId?: number) => {
    if (!enabled) return
    setDropdownOpen(false)
    onOpenChat?.(
      conversationId != null &&
        Number.isFinite(conversationId) &&
        conversationId > 0
        ? { conversationId }
        : undefined,
    )
  }

  const badgeClass =
    badgeSize === 'kanban'
      ? classes.unreadBadge_kanban
      : badgeSize === 'card'
        ? classes.unreadBadge_card
        : classes.unreadBadge_table

  const linkWrapperClass = cn(
    classes.linkWrapper,
    classes.chatLinkWrapper,
    // Белый диск в стопке — только таблица/канбан; в карточке лида как раньше.
    badgeSize !== 'card' && classes.linkWrapper_stacked,
    badgeSize === 'kanban' && classes.linkWrapper_kanban,
    badgeSize === 'card' && classes.linkWrapper_card,
  )

  const iconStyle: CSSProperties = {
    fill: enabled ? iconColor : disabledIconColor,
  }

  const button = (
    <div
      className={linkWrapperClass}
      style={{ cursor: enabled ? 'pointer' : 'default' }}
      role="button"
      tabIndex={enabled ? 0 : -1}
      aria-label={label}
      aria-disabled={!enabled}
      aria-expanded={
        enabled && dialogCount > 0 ? dropdownOpen : undefined
      }
      aria-haspopup={enabled && dialogCount > 0 ? 'menu' : undefined}
      onKeyDown={(e) => {
        if (!enabled || dialogCount === 0) return
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        e.stopPropagation()
        setDropdownOpen((prev) => !prev)
      }}
    >
      <Icon style={iconStyle} />
      {unreadLabel ? (
        <span className={cn(classes.unreadBadge, badgeClass)} aria-hidden>
          {unreadLabel}
        </span>
      ) : null}
    </div>
  )

  const wrapTooltip = (node: ReactNode, title: string | null | undefined) => {
    if (!title) {
      return node
    }
    return (
      <TooltipWrapper title={title} {...tooltipProps}>
        {node}
      </TooltipWrapper>
    )
  }

  if (!enabled) {
    return wrapTooltip(button, disabledTooltip ?? label)
  }

  if (dialogCount === 0) {
    return wrapTooltip(button, enabledTooltip ?? label)
  }

  // Всегда dropdown (даже при 1 диалоге): Wazzup — аккаунт → канал; VK/TG/MAX — список.
  const renderMenuLabel = (
    name: string,
    unreadCount: number,
    isAccessible: boolean,
    options?: {
      typeLabel?: string | null
      channelType?: string | null
      /** Аккаунт Wazzup — показать лого WZ. */
      showWazzupAccountIcon?: boolean
    },
  ) => {
    const tabUnread = formatCrmChatUnreadBadge(Number(unreadCount ?? 0))
    const channelType = options?.channelType?.trim() || null
    const typeLabel = options?.typeLabel
    const showWazzupAccountIcon = Boolean(options?.showWazzupAccountIcon)
    const menuLabel = (
      <div
        className={cn(classes.providerChatMenuItem, {
          [classes.providerChatMenuItemLocked]: !isAccessible,
        })}
      >
        {channelType ? (
          <WazzupChannelTypeMark
            type={channelType}
            size="sm"
            className={classes.providerChatMenuChannelIcon}
          />
        ) : showWazzupAccountIcon ? (
          <span className={classes.providerChatMenuAccountIcon} aria-hidden>
            <WazzupIcon className={classes.providerChatMenuAccountIconImg} />
          </span>
        ) : null}
        <span className={classes.providerChatMenuMain}>
          <span className={classes.providerChatMenuName}>{name}</span>
          {typeLabel ? (
            <span className={classes.providerChatMenuTypeBadge}>{typeLabel}</span>
          ) : null}
        </span>
        <span className={classes.providerChatMenuMeta}>
          {isAccessible && tabUnread ? (
            <span className={classes.providerChatMenuBadge}>{tabUnread}</span>
          ) : null}
          {!isAccessible ? (
            <span className={classes.providerChatMenuLock} aria-hidden>
              <LockIcon />
            </span>
          ) : null}
        </span>
      </div>
    )

    if (!isAccessible && accessDeniedTooltip) {
      return (
        <TooltipWrapper
          placement="top"
          title={accessDeniedTooltip}
          {...tooltipProps}
        >
          {menuLabel}
        </TooltipWrapper>
      )
    }

    return menuLabel
  }

  const buildDialogItem = (
    dialog: CrmProviderChatMenuDialog,
  ): NonNullable<MenuProps['items']>[number] => {
    const isAccessible = dialog.isAccessible !== false
    return {
      key: dialog.key,
      label: renderMenuLabel(dialog.label, dialog.unread_count, isAccessible),
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation()
        if (!isAccessible) {
          return
        }
        openConversation(dialog.conversation_id)
      },
    }
  }

  const buildChannelItem = (
    channel: CrmProviderChatMenuChannel,
  ): NonNullable<MenuProps['items']>[number] => {
    const isAccessible = channel.isAccessible !== false
    const labelOptions = {
      typeLabel: channel.typeLabel,
      channelType: channel.channelType,
    }

    // Один чат на канал — клик по каналу сразу открывает диалог.
    if (channel.conversation_id != null) {
      return {
        key: channel.key,
        label: renderMenuLabel(
          channel.label,
          channel.unread_count,
          isAccessible,
          labelOptions,
        ),
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          if (!isAccessible) {
            return
          }
          openConversation(channel.conversation_id)
        },
      }
    }

    return {
      key: channel.key,
      label: renderMenuLabel(
        channel.label,
        channel.unread_count,
        isAccessible,
        labelOptions,
      ),
      disabled: !isAccessible,
      children: channel.dialogs.map(buildDialogItem),
    }
  }

  const menuItems: MenuProps['items'] = menuAccounts.map((account) => {
    const isAccessible = account.isAccessible !== false
    const isWazzupNested = (account.channels?.length ?? 0) > 0

    if (isWazzupNested) {
      return {
        key: account.key,
        label: renderMenuLabel(
          account.label,
          account.unread_count,
          isAccessible,
          { showWazzupAccountIcon: true },
        ),
        disabled: !isAccessible,
        children: account.channels!.map(buildChannelItem),
      }
    }

    return {
      key: account.key,
      label: renderMenuLabel(account.label, account.unread_count, isAccessible),
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation()
        if (!isAccessible) {
          return
        }
        openConversation(account.conversation_id)
      },
    }
  })

  const hasAccountChannelNesting = menuAccounts.some(
    (account) => (account.channels?.length ?? 0) > 0,
  )
  const hasChannelDialogNesting = menuAccounts.some((account) =>
    (account.channels ?? []).some(
      (channel) =>
        channel.conversation_id == null && channel.dialogs.length > 1,
    ),
  )

  const panelTitles = hasAccountChannelNesting
    ? hasChannelDialogNesting
      ? ['Выберите аккаунт', 'Выберите канал', 'Выберите чат']
      : ['Выберите аккаунт', 'Выберите канал']
    : ['Выберите чат']

  return (
    <CascadingDropdown
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      items={menuItems}
      placement="bottomRight"
      rootDataAttr="data-crm-provider-chat-dropdown"
      overlayClassName={classes.providerChatDropdown}
      panelTitles={panelTitles}
    >
      {button}
    </CascadingDropdown>
  )
}

export default CrmProviderChatButton
